import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { sendAccountIdentityVerificationCode, unbindAccountIdentity } from '../../../store/request';
import type { AccountIdentityType } from '../../../types/account';
import { normalizeIdentity } from '../../../utils/identity';
import useCountdown from '../../../hooks/useCountDown';
import { maskPhone } from './userCenterData';

type UnbindIdentityModalProps = {
  open: boolean;
  identityType: AccountIdentityType;
  identity: string;
  onClose: () => void;
  onSuccess?: () => void;
};

export function UnbindIdentityModal({
  open,
  identityType,
  identity,
  onClose,
  onSuccess
}: UnbindIdentityModalProps) {
  const { t } = useTranslation();
  const { countdown, isRunning, startCountdown, resetCountdown } = useCountdown();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);

  const normalized = normalizeIdentity(identity);
  const display =
    identityType === 'PHONE' ? maskPhone(normalized) : normalized || '—';

  useEffect(() => {
    if (!open) return;
    setCode('');
    setSubmitting(false);
    setSending(false);
    resetCountdown();
    // 仅在打开时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, identityType, identity]);

  const sendCode = async () => {
    if (isRunning || sending || !normalized) return;
    setSending(true);
    try {
      const res = await sendAccountIdentityVerificationCode({
        identityType,
        identity: normalized
      });
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        startCountdown();
      } else if (res.code !== -1) {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    const nextCode = code.trim();
    if (!/^[0-9]{6}$/.test(nextCode)) {
      message.warning(t('userCenter.unbind_code_invalid'));
      return;
    }
    if (!normalized) {
      message.warning(t('userCenter.unbind_identity_missing'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await unbindAccountIdentity({
        identityType,
        identity: normalized,
        code: nextCode
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('userCenter.unbind_failed'));
        return;
      }
      message.success(res.message || t('userCenter.unbind_success'));
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('userCenter.unbind_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        identityType === 'PHONE' ? t('userCenter.unbind_phone_title') : t('userCenter.unbind_email_title')
      }
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={440}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="primary"
            danger
            className="rounded-lg"
            loading={submitting}
            onClick={submit}
          >
            {t('userCenter.unbind_confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-1">
        <p className="mb-0 text-sm leading-relaxed text-slate-500">{t('userCenter.unbind_hint')}</p>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">
            {identityType === 'PHONE' ? t('userCenter.phone') : t('layout.menu_email')}
          </div>
          <Input value={display} disabled />
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('login.code_label')}</div>
          <div className="flex gap-2">
            <Input
              value={code}
              maxLength={6}
              inputMode="numeric"
              placeholder={t('login.code_placeholder')}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <Button className="shrink-0 rounded-lg" loading={sending} disabled={isRunning} onClick={sendCode}>
              {isRunning ? `${countdown}s` : t('login.send_code')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
